import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import dayjs from "dayjs";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// ICONS
import TodayIcon from "@mui/icons-material/Today";
import DateRangeIcon from "@mui/icons-material/DateRange";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

// DATE PICKER
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const datePickerVariants = {
  hidden: { opacity: 0, height: 0, marginTop: 0 },
  visible: {
    opacity: 1,
    height: "auto",
    marginTop: 16,
    transition: {
      opacity: { duration: 0.3 },
      height: { duration: 0.3 },
      marginTop: { duration: 0.3 },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    marginTop: 0,
    transition: {
      opacity: { duration: 0.2 },
      height: { duration: 0.3 },
      marginTop: { duration: 0.3 },
    },
  },
};

const DateFilterOption = ({
  icon: Icon,
  label,
  value,
  currentValue,
  onClick,
}) => {
  const theme = useTheme();
  const isSelected = currentValue === value;

  return (
    <motion.div variants={itemVariants}>
      <Chip
        icon={
          <Icon
            sx={{
              color: isSelected
                ? "inherit"
                : alpha(theme.palette.text.primary, 0.7),
            }}
          />
        }
        label={label}
        onClick={() => onClick(value)}
        color={isSelected ? "primary" : "default"}
        variant={isSelected ? "filled" : "outlined"}
        sx={{
          m: 0.5,
          px: 1,
          border: isSelected
            ? "none"
            : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          fontWeight: isSelected ? 500 : 400,
          "& .MuiChip-label": {
            px: 1,
          },
          transition: "all 0.2s ease-in-out",
          boxShadow: isSelected
            ? `0 2px 4px ${alpha(theme.palette.primary.main, 0.25)}`
            : "none",
          "&:hover": {
            backgroundColor: isSelected
              ? theme.palette.primary.main
              : alpha(theme.palette.action.hover, 0.1),
            boxShadow: isSelected
              ? `0 3px 6px ${alpha(theme.palette.primary.main, 0.3)}`
              : `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`,
          },
        }}
      />
    </motion.div>
  );
};

const DateFilter = ({ selectedValue, onFilterChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [alignment, setAlignment] = useState("today");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [dateStart, setDateStart] = useState(dayjs().startOf("day"));
  const [dateStartString, setDateStartString] = useState(null);
  const [dateEnd, setDateEnd] = useState(dayjs());
  const [dateEndString, setDateEndString] = useState(null);

  useEffect(() => {
    const dateFiltersObj = {
      dateStartString: dateStartString,
      dateEndString: dateEndString,
    };
    onFilterChange(dateFiltersObj);
  }, [dateStartString, dateEndString]);

  const handleAlignmentChange = (newAlignment) => {
    let dateFiltersObj;

    switch (newAlignment) {
      case "today":
        setShowCustomDate(false);
        const startOfToday = dayjs()
          .startOf("day")
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString();
        const endOfToday = dayjs()
          .endOf("day")
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString();
        setAlignment(newAlignment);
        dateFiltersObj = {
          dateStartString: startOfToday,
          dateEndString: endOfToday,
        };
        onFilterChange(dateFiltersObj);
        break;
      case "yesterday":
        setShowCustomDate(false);

        // Create yesterday's date in local time
        const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");

        // Create explicit ISO strings with the correct local day boundaries
        const startOfYesterday = `${yesterday}T00:00:00-03:00`;
        const endOfYesterday = `${yesterday}T23:59:59.999-03:00`;

        console.log("startOfYesterday", startOfYesterday);
        console.log("endOfYesterday", endOfYesterday);

        setAlignment(newAlignment);
        dateFiltersObj = {
          dateStartString: startOfYesterday,
          dateEndString: endOfYesterday,
        };
        onFilterChange(dateFiltersObj);
        break;
      case "last_seven":
        setShowCustomDate(false);
        const startOfLastSevenDays = dayjs()
          .subtract(7, "day")
          .startOf("day")
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString();
        const endOfLastSevenDays = dayjs()
          .endOf("day")
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString();
        setAlignment(newAlignment);
        dateFiltersObj = {
          dateStartString: startOfLastSevenDays,
          dateEndString: endOfLastSevenDays,
        };
        onFilterChange(dateFiltersObj);
        break;
      case "month":
        setShowCustomDate(false);
        const startOfMonth = dayjs()
          .startOf("month")
          .set({ hour: 0, minute: 0, second: 0 })
          .toISOString();
        const endOfMonth = dayjs()
          .endOf("month")
          .set({ hour: 23, minute: 59, second: 59 })
          .toISOString();
        setAlignment(newAlignment);
        dateFiltersObj = {
          dateStartString: startOfMonth,
          dateEndString: endOfMonth,
        };
        onFilterChange(dateFiltersObj);
        break;
      case "custom":
        setShowCustomDate(true);
        setAlignment(newAlignment);
        break;
      default:
        break;
    }
  };

  return (
    <Grid item xs={12} sm={12}>
      <Box>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color="text.primary"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <DateRangeIcon fontSize="small" color="primary" />
            Período
          </Typography>
        </Box>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            justifyContent="center"
            sx={{ mb: 1 }}
          >
            <DateFilterOption
              icon={TodayIcon}
              label="Hoje"
              value="today"
              currentValue={alignment}
              onClick={handleAlignmentChange}
            />
            <DateFilterOption
              icon={AccessTimeIcon}
              label="Ontem"
              value="yesterday"
              currentValue={alignment}
              onClick={handleAlignmentChange}
            />
            <DateFilterOption
              icon={ViewWeekIcon}
              label="Últimos 7 dias"
              value="last_seven"
              currentValue={alignment}
              onClick={handleAlignmentChange}
            />
            <DateFilterOption
              icon={CalendarMonthIcon}
              label="Esse mês"
              value="month"
              currentValue={alignment}
              onClick={handleAlignmentChange}
            />
            <DateFilterOption
              icon={DateRangeIcon}
              label="Selecionar Período"
              value="custom"
              currentValue={alignment}
              onClick={handleAlignmentChange}
            />
          </Stack>
        </motion.div>

        <AnimatePresence>
          {showCustomDate && (
            <motion.div
              key="custom-date-pickers"
              variants={datePickerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.light, 0.05),
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="pt-br"
                    >
                      <DateTimePicker
                        label="De"
                        value={dateStart}
                        onChange={(newValue) => {
                          const date = new Date(new Date(newValue.$d));
                          setDateStart(newValue);
                          setDateStartString(date.toISOString());

                          const dateFiltersObj = {
                            dateStartString: date.toISOString(),
                            dateEndString: dateEndString,
                          };
                          onFilterChange(dateFiltersObj);
                        }}
                        ampm={false} // Use 24-hour format
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: theme.palette.background.paper,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="pt-br"
                    >
                      <DateTimePicker
                        label="Até"
                        value={dateEnd}
                        onChange={(newValue) => {
                          const date = new Date(new Date(newValue.$d));
                          setDateEnd(newValue);
                          setDateEndString(date.toISOString());

                          const dateFiltersObj = {
                            dateStartString: dateStartString,
                            dateEndString: date.toISOString(),
                          };
                          onFilterChange(dateFiltersObj);
                        }}
                        ampm={false} // Use 24-hour format
                        format="DD/MM/YYYY HH:mm"
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                            sx: {
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: theme.palette.background.paper,
                              },
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Grid>
  );
};

DateFilter.displayName = "Data";

export default DateFilter;
